// src/content/content.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getContext } from '../api/content';

// ---------- helpers ----------
function normalizeItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.articles)) return payload.articles; // { articles: [...] }
  if (Array.isArray(payload.items)) return payload.items;       // { items: [...] }
  return Object.values(payload);
}

function getKey(it) {
  const k = it?.id ?? it?._id ?? it?.content_id;
  return k != null ? String(k) : '';
}

// ---------- thunk ----------
export const fetchContent = createAsyncThunk(
  'content/getContext',
  async (args, { rejectWithValue }) => {
    try {
      // args may include { type, tags, label, id, ... } — pass through as your API expects
      const data = await getContext(args);
      return data;
    } catch (err) {
      // keep message small and serializable
      return rejectWithValue(err?.message || 'Failed to load content');
    }
  }
);

// ---------- slice ----------
const initialState = {
  status: 'idle',     // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  byId: {},           // { [id]: item }
  byCategory: {},     // { [type]: item[] }
};

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    // optional helper to upsert a single item
    upsertOne(state, action) {
      const key = getKey(action.payload);
      if (key) state.byId[key] = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContent.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchContent.fulfilled, (state, action) => {
        const type = action.meta?.arg?.type || 'news';
        const items = normalizeItems(action.payload);

        state.byCategory[type] = items;

        items.forEach((it) => {
          const key = getKey(it);
          if (key) state.byId[key] = it;
        });

        state.status = 'succeeded';
      })
      .addCase(fetchContent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Failed to load content';
      });
  },
});

export const { upsertOne } = contentSlice.actions;
export default contentSlice.reducer;